import { FieldError, FieldHint, Input, Label, Textarea } from '@recipes/ui';
import { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from 'react';

export interface FormFieldProps {
  label: ReactNode;
  /** Error message from react-hook-form (`errors.x?.message`). */
  error?: string;
  hint?: ReactNode;
  required?: boolean;
  /** Text shown next to the label for optional fields. */
  optionalLabel?: string;
  className?: string;
  /** A single form control; it receives id/aria-describedby/aria-invalid. */
  children: ReactElement<Record<string, unknown>>;
}

/** Label + control + hint + error wiring (ids and aria attributes) for form controls. */
export function FormField({
  label,
  error,
  hint,
  required,
  optionalLabel,
  className,
  children,
}: FormFieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');
  const supportsInvalidProp = children.type === Input || children.type === Textarea;
  const control = isValidElement(children)
    ? cloneElement(children, {
        id: (children.props.id as string | undefined) ?? id,
        'aria-describedby': describedBy || undefined,
        'aria-invalid': error ? true : undefined,
        ...(supportsInvalidProp ? { invalid: error ? true : undefined } : {}),
        'aria-required': required || undefined,
      })
    : children;
  return (
    <div className={className}>
      <Label htmlFor={(children.props.id as string | undefined) ?? id} className="mb-1.5 block">
        {label}
        {required && (
          <span aria-hidden className="ml-0.5 text-destructive">
            *
          </span>
        )}
        {!required && optionalLabel && (
          <span className="ml-1 text-xs font-normal text-muted-foreground">({optionalLabel})</span>
        )}
      </Label>
      {control}
      <FieldHint id={hintId}>{hint}</FieldHint>
      <FieldError id={errorId}>{error}</FieldError>
    </div>
  );
}
